import {hasReducedMotion} from "../utils/a11y";

interface StaggeredGridObserverConfig {
	selector?: string;
}

const MOBILE_BREAKPOINT = 768;
const MOBILE_START_LINE = 1.08;
const MOBILE_SETTLE_LINE = 0.56;
const MOBILE_MIN_OFFSET = 96;
const MOBILE_MAX_OFFSET = 180;

export const StaggeredGridObserver = {
	elements: [] as HTMLElement[],
	observer: null as IntersectionObserver | null,
	activeElements: new Set<HTMLElement>(),
	animationFrame: null as number | null,

	init(config?: StaggeredGridObserverConfig): void {
		const selector = config?.selector ?? '.wv-staggered-columns';
		this.elements = Array.from(document.querySelectorAll(selector));

		if (!this.elements.length) return;

		this.bindClass();
		this.prepareElements();
		this.bindEvents();
	},

	bindClass(): void {
		this.handleIntersect = this.handleIntersect.bind(this);
		this.requestUpdate = this.requestUpdate.bind(this);
		this.animate = this.animate.bind(this);
	},

	prepareElements(): void {
		this.elements.forEach((container) => {
			container
				.querySelectorAll<HTMLElement>('.wv-staggered-column')
				.forEach((column) => {
					// Remove transforms from the former implementation. The column
					// itself remains the stable, untransformed measurement element.
					column.style.removeProperty('transform');
				});
		});
	},

	bindEvents(): void {
		this.observer = new IntersectionObserver(this.handleIntersect, {
			rootMargin: '20% 0px 20% 0px',
			threshold: 0
		});

		this.elements.forEach((element) => {
			this.observer?.observe(element);
		});

		window.addEventListener('scroll', this.requestUpdate, {passive: true});
		window.addEventListener('resize', this.requestUpdate, {passive: true});
		window.visualViewport?.addEventListener('resize', this.requestUpdate, {
			passive: true
		});
	},

	getColumnsPerRow(total: number): number {
		const viewportWidth = window.innerWidth;

		if (viewportWidth < MOBILE_BREAKPOINT) return 1;

		if (viewportWidth < 1280) {
			if (total === 2) return 2;
			if (total === 3) return 3;
			if (total === 4) return 2;
			if (total === 5) return 2;
			if (total === 6) return 3;
		}

		return total;
	},

	handleIntersect(entries: IntersectionObserverEntry[]): void {
		entries.forEach((entry) => {
			const container = entry.target as HTMLElement;

			if (entry.isIntersecting) {
				this.activeElements.add(container);
				container.classList.add('wv-staggered-columns--is-active');
			} else {
				this.activeElements.delete(container);
				container.classList.remove('wv-staggered-columns--is-active');
			}
		});

		this.requestUpdate();
	},

	requestUpdate(): void {
		if (!this.activeElements.size || this.animationFrame !== null) return;

		this.animationFrame = window.requestAnimationFrame(this.animate);
	},

	animate(): void {
		this.animationFrame = null;

		if (hasReducedMotion()) {
			this.activeElements.forEach((container) => {
				this.resetContainer(container);
			});
			return;
		}

		const viewportHeight =
			window.visualViewport?.height ?? window.innerHeight;
		const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

		this.activeElements.forEach((container) => {
			this.updateContainer(container, viewportHeight, isMobile);
		});
	},

	updateContainer(
		container: HTMLElement,
		viewportHeight: number,
		isMobile: boolean
	): void {
		const columns = Array.from(
			container.querySelectorAll<HTMLElement>('.wv-staggered-column')
		);

		const match = container.className.match(/has-(\d+)-columns/);
		const totalColumns = match ? Number.parseInt(match[1], 10) : columns.length;
		const columnsPerRow = this.getColumnsPerRow(totalColumns);

		const updates = columns.map((column, index) => {
			const animatedElement =
				column.querySelector<HTMLElement>(
					'.wv-staggered-column__inner-blocks'
				) ?? column;

			const columnIndex = index % columnsPerRow;

			const referenceColumn = columns[columnIndex] ?? column;

			const offset = isMobile
				? this.getMobileOffset(column, viewportHeight)
				: this.getDesktopOffset(
					referenceColumn,
					index,
					columnsPerRow,
					totalColumns,
					viewportHeight
				);

			return {animatedElement, offset};
		});

		updates.forEach(({animatedElement, offset}) => {
			animatedElement.style.transform = `translate3d(0, ${offset.toFixed(
				2
			)}px, 0)`;
		});
	},

	getMobileOffset(column: HTMLElement, viewportHeight: number): number {
		const rect = column.getBoundingClientRect();
		const startLine = viewportHeight * MOBILE_START_LINE;
		const settleLine = viewportHeight * MOBILE_SETTLE_LINE;
		const progress = this.clamp(
			(startLine - rect.top) / (startLine - settleLine),
			0,
			1
		);

		// Ease-out makes the card catch up quickly and then settle softly.
		const easedProgress = 1 - Math.pow(1 - progress, 2);
		const maximumOffset = this.clamp(
			viewportHeight * 0.2,
			MOBILE_MIN_OFFSET,
			MOBILE_MAX_OFFSET
		);

		return (1 - easedProgress) * maximumOffset;
	},

	getDesktopOffset(
		column: HTMLElement,
		index: number,
		columnsPerRow: number,
		totalColumns: number,
		viewportHeight: number
	): number {
		const rect = column.getBoundingClientRect();
		const rawProgress = (rect.top + rect.height / 2) / viewportHeight;
		const progress = this.clamp(rawProgress, 0, 1);
		const columnIndex = index % columnsPerRow;

		let direction = columnIndex % 2 === 0 ? -1 : 1;

		if (columnsPerRow === 3) {
			direction = columnIndex === 1 ? 1 : -1;
		}

		let intensity = 25;

		if (window.innerWidth >= 1280) {
			intensity = Math.max(25, 50 - totalColumns * 5);
		} else if (window.innerWidth >= 1024) {
			intensity = 35;
		}

		return direction * (progress - 0.5) * intensity;
	},

	resetContainer(container: HTMLElement): void {
		container
			.querySelectorAll<HTMLElement>(
				'.wv-staggered-column__inner-blocks'
			)
			.forEach((element) => {
				element.style.removeProperty('transform');
			});
	},

	clamp(value: number, minimum: number, maximum: number): number {
		return Math.max(minimum, Math.min(maximum, value));
	}
};
